import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import { ROLE_STATUS, metaFor, type AdminRole } from "@/lib/adminSystem";

const columns: Column<AdminRole>[] = [
  {
    key: "name",
    header: "Role",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1 max-w-sm">{row.description || "—"}</p>
      </div>
    ),
  },
  { key: "users", header: "Users", render: (row) => <span className="text-sm text-foreground-700">{row.users_count}</span> },
  { key: "perms", header: "Permissions", render: (row) => <span className="text-sm text-foreground-700">{(row.permissions || []).length}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(ROLE_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<AdminRole> = {
  table: "admin_roles",
  orderBy: "name",
  eyebrow: "Administration",
  title: "Roles & Permissions",
  subtitle: "Define what each kind of admin can see and do — permissions are grouped by role and applied to every user.",
  searchPlaceholder: "Search roles…",
  searchKeys: ["name", "description"],
  filters: [{ key: "status", label: "Status", options: optionsFrom(ROLE_STATUS) }],
  statusMap: ROLE_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Roles", value: rows.length, icon: "ri-lock-password-line", tone: "primary" },
    { label: "Active", value: rows.filter((row) => row.status === "active").length, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "Users assigned", value: rows.reduce((sum, row) => sum + Number(row.users_count || 0), 0), icon: "ri-team-line", tone: "secondary" },
    { label: "Permissions", value: rows.reduce((sum, row) => sum + (row.permissions || []).length, 0), icon: "ri-key-2-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "name", label: "Role name" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "permissions", label: "Permissions", type: "tags", placeholder: "Bookings, Calendar, Reports…" },
    { key: "users_count", label: "Users", type: "number" },
    { key: "status", label: "Status", type: "select", options: optionsFrom(ROLE_STATUS) },
  ],
  createFields: [
    { key: "name", label: "Role name" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "permissions", label: "Permissions", type: "tags", placeholder: "Bookings, Calendar, Reports…" },
  ],
  newDefaults: { name: "", description: "", permissions: [], users_count: 0, status: "active" },
  emptyTitle: "No roles yet",
  emptyMessage: "Create roles such as Finance or Content Editor and list the permissions each one holds.",
  rowTitle: (row) => row.name,
  rowExtra: (row) =>
    (row.permissions || []).length > 0 ? (
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
          Permissions
        </p>
        <div className="flex flex-wrap gap-2">
          {(row.permissions || []).map((permission) => (
            <span
              key={permission}
              className="inline-flex items-center gap-1.5 text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 px-3 py-1.5 rounded-full"
            >
              <i className="ri-key-line"></i>
              {permission}
            </span>
          ))}
        </div>
      </div>
    ) : null,
};

export default function AdminRoles() {
  return <ResourceModule config={config} />;
}